import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";
import { getAIDashboards } from "../queries/ai.graphql";
import { useWorkspaceFeatureFlags } from "../contexts/global/WorkspaceContext";
import decodeHTMLEntities from "../util/decodeHTMLEntities";

/**
 * Hook to extract all AI-created dashboards from chat history
 * @returns {Record<string, any>} { dashboards: Array, loading: boolean, error: Error, selectedDashboardId: string }
 */
const useAIDashboards = () => {
  const { enableAIChat, enableAskTeamformV2 } = useWorkspaceFeatureFlags();

  // Dashboards are listed regardless of v1/v2; each item links to the right
  // renderer based on its own format. Gate on AI chat (v1) or Ask v2.
  const {
    data: dashboardsData,
    loading,
    error,
  } = useQuery(getAIDashboards, {
    fetchPolicy: "cache-and-network",
    skip: !enableAIChat && !enableAskTeamformV2,
  });

  const dashboards = useMemo(() => {
    if (!dashboardsData?.dashboards?.length) {
      return [];
    }

    // v2 dashboards (format "liquid-v1") render on the v2 page; hide them when
    // Ask v2 is off so v1-only workspaces don't list unreachable dashboards.
    const visible = enableAskTeamformV2
      ? dashboardsData.dashboards
      : dashboardsData.dashboards.filter(
          ({ format }) => format !== "liquid-v1"
        );

    // Convert map to array and sort by creation date (most recent first)
    return visible
      .map((dashboard) => ({
        ...dashboard,
        title: decodeHTMLEntities(dashboard.title),
        createdAt: new Date(parseInt(dashboard.createdAt, 10)),
        updatedAt: new Date(parseInt(dashboard.updatedAt, 10)),
      }))
      .sort((a, b) => b.createdAt - a.createdAt)
      .map(({ messageIndex, ...dashboard }) => dashboard); // Remove internal fields from final output
  }, [dashboardsData, enableAskTeamformV2]);

  return {
    dashboards,
    loading,
    error,
  };
};

export default useAIDashboards;
